import React, {useCallback, useState} from "react";
import {MainLayout} from "../../layouts/MainLayout";

import useFetchEmployees from "../../hooks/useFetchEmployees";
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import {EmployeePageContent, EmployeePanel} from "./styles";
import {employmentStatusesPayrollActionsVisible, FETCH_STATUS_IDLE, FETCH_STATUS_LOADING} from "../../utils/constants";
import {useTranslation} from "react-i18next";
import ImportantMessage from "./ImportantMessage/importantMessage";
import useFetchPayrolls from "../../hooks/useFetchPayrolls";
import SalaryUpdateRequested from "./SalaryUpdate";
import {PopupAskForConfirmation, PopupLoading, PopupWithMessage} from "../../components";
import {
    allPayrollsFetchError,
    allPayrollsFetchState,
    selectCurrentMonthPayrollByEmployee
} from "../../app/payroll/selectors";
import {
    allEmployeesFetchError,
    allEmployeesFetchState,
    selectEmployeeById,
    updateEmployeeError
} from "../../app/employee/selectors";
import ContractTermination from "./ContractTermination";
import RegistrationActions from "./RegistrationActions";
import EmployeeInfo from "./EmployeeInfo";
import PayrollsHistory from "./PayrollsHistory";
import PendingPayrolls from "./PendingPayrolls";
import {deleteExpiredEorEmployee} from "../../app/employee/actions";
import {AppDispatch} from "../../app/store";
import {selectAdminUser} from "../../app/adminUser/selectors";
import {ResDeleteEorExpiredEmployee} from "../../app/interfaces";

export const Employee = () => {
    const [isConfirmationOpen, setConfirmationOpen] = useState(false)
    const dispatch = useDispatch<AppDispatch>();
    useFetchEmployees()
    useFetchPayrolls()

    const params = useParams()
    const id = params.id || ''

    const employee = useSelector(selectEmployeeById(id))
    const adminUser = useSelector(selectAdminUser);

    const currentMonthPayroll = useSelector(selectCurrentMonthPayrollByEmployee(id))

    const employeeUpdateError = useSelector(updateEmployeeError);
    const employeeFetchError = useSelector(allEmployeesFetchError);
    const employeeFetchState = useSelector(allEmployeesFetchState);

    const payrollFetchError = useSelector(allPayrollsFetchError)
    const payrollFetchState = useSelector(allPayrollsFetchState)

    const { t } = useTranslation();
    const navigate = useNavigate();

    const handleCancelEorEmployeeRequest = useCallback(() => {
        setConfirmationOpen(true);
    }, []);

    const handleConfirmDeleteEorEmployee = useCallback(() => {
        if (employee && employee.company && employee._id && employee.quote) {
            dispatch(deleteExpiredEorEmployee({
                companyId: employee.company._id,
                employeeId: employee._id,
                employeeName: `${employee.first_name} ${employee.last_name}`,
                adminUser: `${adminUser.first_name} ${adminUser.last_name}`
            }))
            .then((resultAction) => {
                if (deleteExpiredEorEmployee.fulfilled.match(resultAction)) {
                    const response = resultAction.payload as ResDeleteEorExpiredEmployee;
                    if (response.deleted) {
                        navigate('/employees')
                    }
                }
            })
            setConfirmationOpen(false);
        }
    }, [adminUser.first_name, adminUser.last_name, dispatch, employee, navigate]);

    if (
        employeeFetchState === FETCH_STATUS_IDLE ||
        employeeFetchState === FETCH_STATUS_LOADING ||
        payrollFetchState === FETCH_STATUS_IDLE ||
        payrollFetchState === FETCH_STATUS_LOADING

    ) {
        return <MainLayout>
            <PopupLoading isOpen={true} />
        </MainLayout>
    }

    if(employeeFetchError || !employee || employeeUpdateError){
        const errorMessage = employeeFetchError || employeeUpdateError || t('error_retrieving_employee') as string;
        return (
            <MainLayout>
                <PopupWithMessage
                    isOpen={true}
                    title={t('error_message_title') as string}
                    subtitle={errorMessage}
                    buttonTitle={'Ok'}
                    buttonAction={() => navigate('/employees')}
                    type="error"
                />
            </MainLayout>
        );
    }

    if(payrollFetchError){
        const errorMessage = payrollFetchError || t('error_retrieving_payrolls') as string;
        return (
            <MainLayout>
                <PopupWithMessage
                    isOpen={true}
                    title={t('error_message_title') as string}
                    subtitle={errorMessage}
                    buttonTitle={'Ok'}
                    buttonAction={() => navigate('/employees')}
                    type="error"
                />
            </MainLayout>
        );
    }

    return <MainLayout>
        {employee.hasOwnProperty('salary_change_requested') && <SalaryUpdateRequested employee={employee}/>}
        <EmployeePageContent>
            <EmployeeInfo employee={employee}/>
            <EmployeePanel>
                <ImportantMessage
                    employee={employee}
                    currentMonthPayroll={currentMonthPayroll}
                    onCancelEorRequest={handleCancelEorEmployeeRequest}
                />
                {isConfirmationOpen && (
                    <PopupAskForConfirmation
                        isOpen={true}
                        subtitle={t('quote_cancel_message') as string}
                        onConfirm={handleConfirmDeleteEorEmployee}
                        onCancel={() => setConfirmationOpen(false)}
                    />
                )}

                <ContractTermination employee={employee}/>
                <RegistrationActions employee={employee}/>
                {employmentStatusesPayrollActionsVisible.includes(employee.status) && <PendingPayrolls employee={employee}/>}
                {employmentStatusesPayrollActionsVisible.includes(employee.status) && <PayrollsHistory employee={employee}/>}
            </EmployeePanel>
        </EmployeePageContent>
    </MainLayout>
}

export default Employee;
